import { CONVERSION_REFERENCE_DATA } from "./ConversionReferenceData";

export type ConversionCurrency = "USD" | "BTC";

export type PerspectiveInsight = {
  id: string;
  title: string;
  value: string;
  note: string;
  relevanceValue?: number;
};

export type PerspectiveSection = {
  id: string;
  title: string;
  description: string;
  insights: PerspectiveInsight[];
};

export type PerspectiveSnapshot = {
  heading: string;
  subheading: string;
  dataAsOf: string;
  sections: PerspectiveSection[];
};

export type HomeComparisonItem = {
  id: string;
  label: string;
  usdValue: number;
};

export const CONVERSION_CURRENCIES: ConversionCurrency[] = ["USD", "BTC"];
export const PERSPECTIVE_INPUT_DECIMALS = 10;
export const PERSPECTIVE_RELEVANCE_MIN = 1;
export const PERSPECTIVE_RELEVANCE_MAX = 10_000_000;
export const PERSPECTIVE_RELEVANCE_RANGE = {
  min: PERSPECTIVE_RELEVANCE_MIN,
  max: PERSPECTIVE_RELEVANCE_MAX,
} as const;

export const HOME_COMPARISON_ITEMS: HomeComparisonItem[] = [
  {
    id: "us-debt",
    label: "US national debt",
    usdValue: CONVERSION_REFERENCE_DATA.usNationalDebtUsd,
  },
  {
    id: "us-federal-budget",
    label: "US federal budget",
    usdValue: CONVERSION_REFERENCE_DATA.usFederalBudgetUsd,
  },
  {
    id: "apple-market-cap",
    label: "Apple market cap",
    usdValue: CONVERSION_REFERENCE_DATA.appleMarketCapUsd,
  },
  {
    id: "nvidia-market-cap",
    label: "NVIDIA market cap",
    usdValue: CONVERSION_REFERENCE_DATA.nvidiaMarketCapUsd,
  },
];

const INPUT_PATTERN = /^[\d\s,.]*\.?[\d\s,]*$/;

const createNumberFormatter = (
  options?: Intl.NumberFormatOptions,
  fallbackOptions?: Intl.NumberFormatOptions,
) => {
  try {
    return new Intl.NumberFormat("en-US", options);
  } catch {
    return new Intl.NumberFormat("en-US", fallbackOptions);
  }
};

const numberFormatter = createNumberFormatter();
const compactNumberFormatter = createNumberFormatter(
  {
    notation: "compact",
    maximumFractionDigits: 0,
  },
  {
    maximumFractionDigits: 0,
  },
);
const compactUsdFormatter = createNumberFormatter(
  {
    style: "currency",
    currency: "USD",
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
  {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
);
const usdFormatter = createNumberFormatter({
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const getValidCurrency = (
  value?: string | null,
): ConversionCurrency | undefined => {
  if (!value) {
    return undefined;
  }

  const upperCased = value.toUpperCase() as ConversionCurrency;
  return CONVERSION_CURRENCIES.includes(upperCased) ? upperCased : undefined;
};

const stripNumericSeparators = (value: string) => value.replace(/[ ,]/g, "");

export const isValidNumericInput = (value: string) => {
  if (!INPUT_PATTERN.test(value)) {
    return false;
  }

  const cleaned = stripNumericSeparators(value);
  const dotMatches = cleaned.match(/\./g);
  if (dotMatches && dotMatches.length > 1) {
    return false;
  }

  return /^\d*\.?\d*$/.test(cleaned);
};

export const parseInputNumber = (value: string): number | undefined => {
  if (!value || value === ".") {
    return undefined;
  }

  const cleaned = stripNumericSeparators(value);
  if (!cleaned || cleaned === ".") {
    return undefined;
  }

  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }

  return parsed;
};

export const formatEditableNumber = (
  value: number,
  fractionDigits = PERSPECTIVE_INPUT_DECIMALS,
): string => {
  if (!Number.isFinite(value)) {
    return "";
  }

  const fixed = value.toFixed(fractionDigits).replace(/\.?0+$/, "");
  const [intPart, fracPart] = fixed.split(".");
  const withGrouping = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return fracPart ? `${withGrouping}.${fracPart}` : withGrouping;
};

const truncateToInt = (value: number) =>
  value >= 0 ? Math.floor(value) : Math.ceil(value);

const formatPercent = (value: number) => `${truncateToInt(value)}%`;

const formatCompactNumber = (value: number) =>
  compactNumberFormatter.format(truncateToInt(value));

const formatCompactUsd = (value: number) =>
  compactUsdFormatter.format(truncateToInt(value));

const formatUsd = (value: number) => usdFormatter.format(truncateToInt(value));

const formatBtc = (value: number, fractionDigits = 8) =>
  `${value.toFixed(fractionDigits).replace(/\.?0+$/, "")} BTC`;

const formatYears = (value: number) => `${truncateToInt(value)} years`;

const formatDays = (value: number) => `${truncateToInt(value)} days`;

const formatSeconds = (value: number) => `${numberFormatter.format(Math.round(value))} seconds`;

const getInvestorTier = (btcValue: number) => {
  if (btcValue < 1) {
    return "shrimp";
  }
  if (btcValue < 10) {
    return "crab";
  }
  if (btcValue < 50) {
    return "octopus";
  }
  if (btcValue < 100) {
    return "fish";
  }
  if (btcValue < 500) {
    return "dolphin";
  }
  if (btcValue < 1_000) {
    return "shark";
  }

  return "whale";
};

export const convertFromUsdInput = (
  usdInput: string,
  btcUsdPrice: number,
): number | undefined => {
  const fullUsdValue = parseInputNumber(usdInput);
  if (fullUsdValue === undefined || btcUsdPrice <= 0) {
    return undefined;
  }

  return fullUsdValue / btcUsdPrice;
};

export const convertFromBtcInput = (
  btcInput: string,
): number | undefined => {
  return parseInputNumber(btcInput);
};

export const getHomeComparisonInBtc = (usdValue: number, btcUsdPrice: number) => {
  if (!Number.isFinite(usdValue) || btcUsdPrice <= 0) {
    return undefined;
  }

  return usdValue / btcUsdPrice;
};

export const buildPerspectiveSnapshot = ({
  btcValue,
  btcUsdPrice,
  btcMarketCapUsd,
}: {
  btcValue: number;
  btcUsdPrice: number;
  btcMarketCapUsd: number;
}): PerspectiveSnapshot => {
  const usdValue = btcValue * btcUsdPrice;
  const takeHomeIncome =
    CONVERSION_REFERENCE_DATA.usMedianIncomePreTaxUsdPerYear *
    CONVERSION_REFERENCE_DATA.medianIncomeTakeHomeRatio;
  const btcPerPerson =
    CONVERSION_REFERENCE_DATA.totalBtcSupplyLimit /
    CONVERSION_REFERENCE_DATA.worldPopulation;
  const marketCapDenominator =
    btcMarketCapUsd > 0 ? btcMarketCapUsd : CONVERSION_REFERENCE_DATA.appleMarketCapUsd;

  const sections: PerspectiveSection[] = [
    {
      id: "macro",
      title: "Macro Scale",
      description: "How large this stack looks against national and global money buckets.",
      insights: [
        {
          id: "btc-market-cap",
          title: "Share of Bitcoin market cap",
          value: `${formatCompactNumber(usdValue / marketCapDenominator)}x`,
          note: `That is ${formatPercent((usdValue / marketCapDenominator) * 100)} of current BTC market cap.`,
          relevanceValue: usdValue / marketCapDenominator,
        },
        {
          id: "us-debt-share",
          title: "US debt exposure",
          value: formatPercent((usdValue / CONVERSION_REFERENCE_DATA.usNationalDebtUsd) * 100),
          note: "Share of total US national debt outstanding.",
          relevanceValue: (usdValue / CONVERSION_REFERENCE_DATA.usNationalDebtUsd) * 100,
        },
        {
          id: "us-m2-share",
          title: "US M2 share",
          value: formatPercent((usdValue / CONVERSION_REFERENCE_DATA.usM2Usd) * 100),
          note: "Share of US broad money supply (M2).",
          relevanceValue: (usdValue / CONVERSION_REFERENCE_DATA.usM2Usd) * 100,
        },
        {
          id: "global-m2-share",
          title: "Global M2 share",
          value: formatPercent((usdValue / CONVERSION_REFERENCE_DATA.globalM2Usd) * 100),
          note: "Share of global broad money supply (M2).",
          relevanceValue: (usdValue / CONVERSION_REFERENCE_DATA.globalM2Usd) * 100,
        },
        {
          id: "global-gdp-share",
          title: "Global GDP share",
          value: formatPercent((usdValue / CONVERSION_REFERENCE_DATA.globalGdpUsd) * 100),
          note: "GDP is yearly output, so this is a yearly-production comparison.",
          relevanceValue: (usdValue / CONVERSION_REFERENCE_DATA.globalGdpUsd) * 100,
        },
      ],
    },
    {
      id: "time-and-work",
      title: "Time and Work",
      description: "Translate the number into paychecks and human effort.",
      insights: [
        {
          id: "median-income-years-pretax",
          title: "Median-income years (pre-tax)",
          value: formatYears(
            usdValue / CONVERSION_REFERENCE_DATA.usMedianIncomePreTaxUsdPerYear,
          ),
          note: "Years of US median gross income.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.usMedianIncomePreTaxUsdPerYear,
        },
        {
          id: "median-income-years-takehome",
          title: "Median-income years (take-home)",
          value: formatYears(usdValue / takeHomeIncome),
          note: "Years of estimated median take-home pay.",
          relevanceValue: usdValue / takeHomeIncome,
        },
        {
          id: "hourly-wage-days",
          title: "Work days at average hourly wage",
          value: formatDays(
            usdValue /
            (CONVERSION_REFERENCE_DATA.hourlyWageUsd *
              CONVERSION_REFERENCE_DATA.workHoursPerDay),
          ),
          note: "No overtime bonus included.",
          relevanceValue:
            usdValue /
            (CONVERSION_REFERENCE_DATA.hourlyWageUsd *
              CONVERSION_REFERENCE_DATA.workHoursPerDay),
        },
        {
          id: "mortgage-payments",
          title: "Monthly mortgage payments",
          value: formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.usAverageMonthlyMortgageUsd,
          ),
          note: "Equivalent count of average monthly US mortgage payments.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.usAverageMonthlyMortgageUsd,
        },
        {
          id: "years-of-rent",
          title: "Years of rent",
          value: formatYears(
            usdValue /
            (CONVERSION_REFERENCE_DATA.usAverageMonthlyRentUsd * 12),
          ),
          note: "Approximate years of average US rent coverage.",
          relevanceValue:
            usdValue /
            (CONVERSION_REFERENCE_DATA.usAverageMonthlyRentUsd * 12),
        },
      ],
    },
    {
      id: "btc-native",
      title: "BTC Native View",
      description: "How this amount fits into Bitcoin's fixed-supply world.",
      insights: [
        {
          id: "supply-share",
          title: "Share of all BTC ever",
          value: formatPercent(
            (btcValue / CONVERSION_REFERENCE_DATA.totalBtcSupplyLimit) * 100,
          ),
          note: "Hard cap math: 21M and not a satoshi more.",
          relevanceValue:
            (btcValue / CONVERSION_REFERENCE_DATA.totalBtcSupplyLimit) * 100,
        },
        {
          id: "fair-share-people",
          title: "People fair-share equivalent",
          value: `${formatCompactNumber(btcValue / btcPerPerson)} people`,
          note: "If BTC were split evenly across 8B people.",
          relevanceValue: btcValue / btcPerPerson,
        },
        {
          id: "block-reward",
          title: "Miner reward equivalent",
          value: `${formatCompactNumber(
            btcValue / CONVERSION_REFERENCE_DATA.currentBlockSubsidyBtc,
          )} blocks`,
          note: "Roughly how many current-subsidy blocks miners need to earn this much BTC.",
          relevanceValue: btcValue / CONVERSION_REFERENCE_DATA.currentBlockSubsidyBtc,
        },
        {
          id: "investor-tier",
          title: "Wallet creature tier",
          value: getInvestorTier(btcValue),
          note: "From shrimp to whale, based on common on-chain holder bucket nicknames.",
        },
      ],
    },
    {
      id: "markets-and-things",
      title: "Markets and Stuff",
      description: "Concrete comparisons for everyday and market intuition.",
      insights: [
        {
          id: "future-price",
          title: "If BTC hits $500k",
          value: formatCompactUsd(
            btcValue * CONVERSION_REFERENCE_DATA.btcBullCasePriceUsd,
          ),
          note: "Same BTC stack, different USD sticker price.",
          relevanceValue: btcValue * CONVERSION_REFERENCE_DATA.btcBullCasePriceUsd,
        },
        {
          id: "pizzas-today",
          title: "Pizzas today",
          value: formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.averagePizzaPriceUsd,
          ),
          note: "Based on current average pizza price.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.averagePizzaPriceUsd,
        },
        {
          id: "pizzas-2010-deal",
          title: "Pizzas at 2010 deal terms",
          value: formatCompactNumber(
            btcValue / CONVERSION_REFERENCE_DATA.btcPerPizzaAtBitcoinPizzaDayDeal,
          ),
          note: "Using 10,000 BTC for 2 pizzas (5,000 BTC each).",
          relevanceValue: btcValue / CONVERSION_REFERENCE_DATA.btcPerPizzaAtBitcoinPizzaDayDeal,
        },
        {
          id: "cars",
          title: "Cars",
          value: formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.averageUsCarPriceUsd,
          ),
          note: "Average US new-car transaction price.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.averageUsCarPriceUsd,
        },
        {
          id: "houses",
          title: "Houses",
          value: formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.averageUsHomePriceUsd,
          ),
          note: "Average US home price benchmark.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.averageUsHomePriceUsd,
        },
        {
          id: "apple-shares",
          title: "Apple shares",
          value: `${formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.appleSharePriceUsd,
          )} AAPL shares`,
          note: "Equivalent number of Apple shares.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.appleSharePriceUsd,
        },
        {
          id: "iphones",
          title: "iPhones",
          value: formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.iphonePriceUsd,
          ),
          note: "Equivalent number of base-model iPhones.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.iphonePriceUsd,
        },
      ],
    },
    {
      id: "commodities-and-indices",
      title: "Commodities and Indices",
      description: "Benchmark against metals, equities, consumption, and balance sheets.",
      insights: [
        {
          id: "gold-kilograms",
          title: "Gold (kilograms)",
          value: `${formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.goldPricePerKgUsd,
          )} kg`,
          note: "Gold value using price per kilogram.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.goldPricePerKgUsd,
        },
        {
          id: "gold-tons",
          title: "Gold (tons)",
          value: `${formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.goldPricePerKgUsd / 1_000,
          )} tons`,
          note: "Same gold value shown in metric tons.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.goldPricePerKgUsd / 1_000,
        },
        {
          id: "gold-cap-share",
          title: "Global gold cap share",
          value: formatPercent(
            (usdValue / CONVERSION_REFERENCE_DATA.globalGoldMarketCapUsd) * 100,
          ),
          note: "Share of total global gold market value.",
          relevanceValue:
            (usdValue / CONVERSION_REFERENCE_DATA.globalGoldMarketCapUsd) * 100,
        },
        {
          id: "sp500-share",
          title: "S&P 500 share",
          value: formatPercent(
            (usdValue / CONVERSION_REFERENCE_DATA.sp500MarketCapUsd) * 100,
          ),
          note: "Share of total S&P 500 market cap.",
          relevanceValue:
            (usdValue / CONVERSION_REFERENCE_DATA.sp500MarketCapUsd) * 100,
        },
        {
          id: "nyc-space",
          title: "NYC space",
          value: `${formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.nycPricePerSquareMeterUsd,
          )} m2`,
          note: "Square meters at NYC average price per m2.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.nycPricePerSquareMeterUsd,
        },
        {
          id: "big-macs",
          title: "Big Macs",
          value: formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.bigMacPriceUsd,
          ),
          note: "Equivalent number of Big Macs.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.bigMacPriceUsd,
        },
        {
          id: "gas-gallons",
          title: "Gasoline gallons",
          value: formatCompactNumber(
            usdValue / CONVERSION_REFERENCE_DATA.gasPricePerGallonUsd,
          ),
          note: "US regular gasoline gallons.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.gasPricePerGallonUsd,
        },
        {
          id: "fed-print-time",
          title: "Fed print time",
          value: formatSeconds(
            usdValue / CONVERSION_REFERENCE_DATA.fedUsdPrintPerSecond,
          ),
          note: "Back-of-the-envelope time versus Fed expansion pace.",
          relevanceValue: usdValue / CONVERSION_REFERENCE_DATA.fedUsdPrintPerSecond,
        },
        {
          id: "central-bank-growth-time",
          title: "Central-bank balance-sheet time",
          value: formatDays(
            usdValue / CONVERSION_REFERENCE_DATA.centralBankBalanceSheetGrowthPerDayUsd,
          ),
          note: "Equivalent days of estimated aggregate central-bank growth.",
          relevanceValue:
            usdValue / CONVERSION_REFERENCE_DATA.centralBankBalanceSheetGrowthPerDayUsd,
        },
      ],
    },
  ];

  return {
    heading: `${formatBtc(btcValue)} represents...`,
    subheading: `${formatUsd(usdValue)} at ${formatUsd(btcUsdPrice)} per BTC`,
    dataAsOf: CONVERSION_REFERENCE_DATA.dataAsOf,
    sections,
  };
};

export const getCompactUsdLabel = (value: number) => formatCompactUsd(value);

export const getCompactBtcLabel = (btcValue: number) =>
  `${formatCompactNumber(btcValue)} BTC`;

// Units removed; values are entered directly.
